import React, { useState } from 'react';
import {
  Calendar, Layers, Users, Eye, CheckSquare, RefreshCw,
  ArrowRight, Check, Minus, ChevronDown, Menu, X,
} from 'lucide-react';
import { Logo } from '../ui/Logo';
import { LandingChrome } from './LandingChrome';
import MosaicHero from './MosaicHero';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface LandingPageProps {
  onGoToLogin?: () => void;
  onGoToSignUp?: () => void;
  isLoggedIn?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Content                                                            */
/* ------------------------------------------------------------------ */

/** PAS — agitate before solving. Written in the reader's words, not ours. */
const PROBLEMS = [
  {
    title: 'You agree to June in January',
    body: 'A month view cannot show you that the week already has a launch, a school holiday and someone else’s trip in it. So you say yes, and find out later.',
  },
  {
    title: 'The clash was visible in March',
    body: 'Conflicts do not appear suddenly. They were always there, just on a screen you had to scroll to. By the time the week view shows you, it is too late to move anything.',
  },
  {
    title: 'The year is spread across four apps',
    body: 'Work in one calendar, family in another, the trip in an email and the deadline in a task list. Nothing holds all of it at once, so nobody can see the shape of the year.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Bring your calendars in',
    body: 'Connect Google both ways, or subscribe to any iCal feed. Nothing has to move — Mozayc sits on top of what you already use.',
  },
  {
    n: '02',
    title: 'Colour-code what matters',
    body: 'A category per part of life, or per person. Projects become bars that span weeks, so commitments read as blocks of time rather than dots on a day.',
  },
  {
    n: '03',
    title: 'See the whole year',
    body: 'Twelve months on one screen, weekday-aligned. Share a space with the people it concerns, print it, or publish one category as a subscription link.',
  },
];

const FEATURES = [
  {
    icon: Calendar,
    title: 'The whole year, one screen',
    body: 'Twelve months side by side, weekday-aligned so every Monday lines up and weekends run as clean stripes.',
  },
  {
    icon: Layers,
    title: 'Six ways to look at it',
    body: 'Year, vertical year, month, week, schedule and board. Same events, whichever altitude the question needs.',
  },
  {
    icon: CheckSquare,
    title: 'Tasks live with events',
    body: 'A kanban board with due dates, priorities, assignees and reminders — beside the calendar, not in a separate app.',
  },
  {
    icon: Users,
    title: 'Shared spaces, real roles',
    body: 'Invite people as admin, editor or viewer. Everyone sees the same year without anyone losing their own.',
  },
  {
    icon: RefreshCw,
    title: 'Two-way Google sync',
    body: 'Plus any iCal feed, and you can publish a category as a read-only link others subscribe to.',
  },
  {
    icon: Eye,
    title: 'Yours to take out',
    body: 'Print a year that fits a page. Export ICS or JSON any time. Privacy mode blurs details when you share a screen.',
  },
];

const AUDIENCES = [
  {
    eyebrow: 'Planning your own year',
    title: 'Hold the whole thing in your head',
    body: 'Freelancers, researchers and anyone whose work runs in projects rather than meetings.',
    points: ['Unlimited events and tasks, free forever', 'Deadlines as bars, not dots', 'Print it and put it on the wall'],
  },
  {
    eyebrow: 'Running a household',
    title: 'Everyone on one page',
    body: 'School terms, holidays, birthdays, who is away when — without losing your own private calendar.',
    points: ['A colour per person', 'Share with the people it concerns', 'Subscribe from Apple or Google'],
  },
  {
    eyebrow: 'A small team',
    title: 'The plan behind the sprint board',
    body: 'Launches, leave, offsites and hiring on one year view, so the shape of the quarter is obvious.',
    points: ['Admin, editor and viewer roles', 'Tasks assigned to people', 'Everyone on the same year, live'],
  },
];

const FAQS = [
  {
    q: 'Is it really free?',
    a: 'Yes, and it stays free. Unlimited events, tasks and tags on your own calendar, all six views, print and export. You pay only for more shared spaces, more people in them, or more imported calendars — never to keep what you have already made.',
  },
  {
    q: 'Does it replace Google Calendar?',
    a: 'It does not have to. Mozayc syncs two ways with Google and reads any iCal feed, so most people run it alongside what they already use. The year view is the thing they were missing, not the meetings.',
  },
  {
    q: 'How long does setup take?',
    a: 'A couple of minutes. Sign up, connect a calendar or skip it, and you are looking at your year. There is no import to wait on and nothing to configure first.',
  },
  {
    q: 'What happens if I stop paying?',
    a: 'Nothing is deleted. You drop back to the free plan and keep every event, task and calendar you created — the paid limits simply apply again.',
  },
  {
    q: 'Can I get my data out?',
    a: 'Any time, without asking. Export to ICS for another calendar or JSON for everything else, and delete your account yourself from settings.',
  },
  {
    q: 'Who can see my calendar?',
    a: 'Only you, unless you share it. Shared spaces are invite-only, and a published category link can be revoked and reissued whenever you want.',
  },
];

export const PLANS = [
  {
    name: 'Free',
    price: { monthly: '€0', annual: '€0' },
    note: 'For one person, forever',
    cta: 'Start free',
    highlight: false,
    features: [
      { t: 'Unlimited events, tasks and tags', ok: true },
      { t: 'All six views, print and export', ok: true },
      { t: 'Up to 8 categories', ok: true },
      { t: '1 imported calendar', ok: true },
      { t: '1 shared space, 2 people', ok: true },
      { t: 'Public subscription links', ok: false },
    ],
  },
  {
    name: 'Pro',
    price: { monthly: '€6', annual: '€5' },
    note: 'For a busy year, or a household',
    cta: 'Start with Pro',
    highlight: true,
    features: [
      { t: 'Everything in Free', ok: true },
      { t: 'Unlimited categories', ok: true },
      { t: '5 imported calendars', ok: true },
      { t: '3 shared spaces, 5 people each', ok: true },
      { t: 'Unlimited subscription links', ok: true },
      { t: 'Priority support', ok: true },
    ],
  },
  {
    name: 'Team',
    price: { monthly: '€5', annual: '€4' },
    unit: 'per person',
    note: 'Minimum 3 people',
    cta: 'Start with Team',
    highlight: false,
    features: [
      { t: 'Everything in Pro', ok: true },
      { t: 'Unlimited shared spaces', ok: true },
      { t: 'Unlimited people per space', ok: true },
      { t: 'Admin, editor and viewer roles', ok: true },
      { t: 'Unlimited imported calendars', ok: true },
      { t: 'Invoiced billing', ok: true },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Sections                                                           */
/* ------------------------------------------------------------------ */

export function Nav({ onGoToLogin, onGoToSignUp, isLoggedIn }: Pick<LandingPageProps, 'onGoToLogin' | 'onGoToSignUp' | 'isLoggedIn'>) {
  const [open, setOpen] = useState(false);
  const links = [
    { label: 'How it works', href: '#how' },
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--lp-grout)] bg-[var(--lp-ground)]/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-5">
        <Logo size="sm" />
        <nav className="hidden flex-1 items-center gap-7 md:flex">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-[13px] text-[var(--lp-mid)] transition-colors hover:text-[var(--lp-ink)]">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto hidden items-center gap-2 md:flex">
          {isLoggedIn ? (
            <button onClick={onGoToLogin} className="btn-primary">Open Mozayc</button>
          ) : (
            <>
              <button onClick={onGoToLogin} className="btn-ghost">Sign in</button>
              <button onClick={onGoToSignUp} className="btn-primary">Start free</button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="ml-auto rounded-lg p-2 text-[var(--lp-mid)] hover:bg-[var(--lp-hover)] md:hidden"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-[var(--lp-grout)] bg-[var(--lp-ground)] px-5 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            {links.map(l => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="rounded-lg px-2 py-2 text-sm text-[var(--lp-mid)] hover:bg-[var(--lp-hover)]">
                {l.label}
              </a>
            ))}
            <div className="mt-2 flex gap-2">
              <button onClick={onGoToLogin} className="btn-ghost flex-1">Sign in</button>
              <button onClick={onGoToSignUp} className="btn-primary flex-1">Start free</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ onGoToSignUp }: Pick<LandingPageProps, 'onGoToSignUp'>) {
  return (
    <MosaicHero>
      <button onClick={onGoToSignUp} className="btn-primary btn-lg group">
        Start free
        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
      <span className="mh-risk">Free forever for one person. No credit card.</span>
    </MosaicHero>
  );
}

function Problem() {
  return (
    <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--t-coral)' }}>The problem</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            A month view cannot answer a year-shaped question
          </h2>
        </div>
        <div className="mt-11 grid gap-px overflow-hidden rounded-xl border border-[var(--lp-grout)] bg-[var(--lp-grout)] md:grid-cols-3">
          {PROBLEMS.map(p => (
            <div key={p.title} className="bg-[var(--lp-ground)] p-7">
              <h3 className="text-[15px] font-medium text-[var(--lp-ink)]">{p.title}</h3>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Solution() {
  return (
    <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow" style={{ color: 'var(--t-teal)' }}>The answer</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            One screen that holds the whole year
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-[var(--lp-mid)]">
            Events become bars that span the days they actually cover. Because the
            months are weekday-aligned, a weekly commitment draws a straight line
            down the page and free weeks are visible from across the room.
          </p>
        </div>
        <figure
          className="m-0 mt-11 w-full overflow-hidden rounded-xl"
          style={{ border: '1px solid var(--lp-grout)', boxShadow: 'var(--lp-shadow)', backgroundColor: 'var(--lp-raise-2)' }}
        >
          {/* WebP is 78 KB against 298 KB for the PNG fallback. The master
              capture lives in assets/screenshots/ so it is not served. */}
          <picture>
            <source srcSet="/hero-year.webp" type="image/webp" />
            <img
              src="/hero-year-1800.png"
              alt="Mozayc in dark mode showing all of 2026 in the year view — colour-coded categories in the sidebar, events spanning weeks across twelve months"
              width={1800}
              height={1041}
              className="block h-auto w-full"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </figure>
      </div>
    </section>
  );
}

function HowItWorks({ onGoToSignUp }: Pick<LandingPageProps, 'onGoToSignUp'>) {
  return (
    <section id="how" className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--t-gold)' }}>How it works</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            Three steps, about two minutes
          </h2>
        </div>
        <div className="mt-11 grid gap-8 md:grid-cols-3">
          {STEPS.map(s => (
            <div key={s.n}>
              <span className="font-mono text-[12px] font-medium text-[var(--lp-accent)]">{s.n}</span>
              <h3 className="mt-2.5 text-[16px] font-medium text-[var(--lp-ink)]">{s.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-11 flex flex-wrap items-center gap-x-5 gap-y-3">
          <button onClick={onGoToSignUp} className="btn-primary group">
            Start free
            <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <span className="text-[13px] text-[var(--lp-low)]">No credit card. Nothing to migrate.</span>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--t-violet)' }}>Features</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            Everything the year view needs behind it
          </h2>
        </div>
        <div className="mt-11 grid gap-px overflow-hidden rounded-xl border border-[var(--lp-grout)] bg-[var(--lp-grout)] sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(f => (
            <div key={f.title} className="bg-[var(--lp-ground)] p-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--lp-accent-soft)]">
                <f.icon className="h-[18px] w-[18px] text-[var(--lp-accent)]" />
              </div>
              <h3 className="mt-4 text-[15px] font-medium text-[var(--lp-ink)]">{f.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Audiences() {
  return (
    <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: 'var(--t-lime)' }}>Who it is for</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            One year view, three kinds of planning
          </h2>
        </div>
        <div className="mt-11 grid gap-px overflow-hidden rounded-xl border border-[var(--lp-grout)] bg-[var(--lp-grout)] lg:grid-cols-3">
          {AUDIENCES.map(a => (
            <div key={a.title} className="flex flex-col bg-[var(--lp-ground)] p-7">
              <p className="eyebrow">{a.eyebrow}</p>
              <h3 className="mt-3 text-balance text-lg font-semibold leading-snug tracking-[-0.01em] text-[var(--lp-ink)]">{a.title}</h3>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{a.body}</p>
              <ul className="mt-5 flex flex-col gap-2.5 border-t border-[var(--lp-grout)] pt-5">
                {a.points.map(p => (
                  <li key={p} className="flex gap-2.5 text-[13px] text-[var(--lp-mid)]">
                    <Check className="mt-[3px] h-3.5 w-3.5 flex-none text-[var(--lp-accent)]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ onGoToSignUp }: Pick<LandingPageProps, 'onGoToSignUp'>) {
  const [annual, setAnnual] = useState(true);
  return (
    <section id="pricing" className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow" style={{ color: 'var(--t-sky)' }}>Pricing</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            Free for your own year. Paid when you share it.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[15px] leading-relaxed text-[var(--lp-mid)]">
            We never cap the events or tasks you create. What you make stays yours,
            and stays visible, whatever you pay.
          </p>
          <div className="mt-8 inline-flex items-center gap-1 rounded-lg border border-[var(--lp-grout)] bg-[var(--lp-raise)] p-1">
            <button type="button" onClick={() => setAnnual(false)} aria-pressed={!annual}
              className={`rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors ${!annual ? 'bg-[var(--lp-raise-2)] text-[var(--lp-ink)] shadow-sm' : 'text-[var(--lp-mid)]'}`}>
              Monthly
            </button>
            <button type="button" onClick={() => setAnnual(true)} aria-pressed={annual}
              className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors ${annual ? 'bg-[var(--lp-raise-2)] text-[var(--lp-ink)] shadow-sm' : 'text-[var(--lp-mid)]'}`}>
              Annual
              <span className="rounded bg-[var(--lp-accent-soft)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--lp-accent)]">2 months free</span>
            </button>
          </div>
        </div>

        <div className="mt-11 grid gap-5 lg:grid-cols-3">
          {PLANS.map(p => (
            <div key={p.name}
              className={`flex flex-col rounded-xl border p-7 ${p.highlight ? 'border-[var(--lp-accent)] shadow-[var(--lp-shadow)]' : 'border-[var(--lp-grout)]'} bg-[var(--lp-ground)]`}>
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-[var(--lp-ink)]">{p.name}</h3>
                {p.highlight && (
                  <span className="rounded-full bg-[var(--lp-accent-soft)] px-2.5 py-1 text-[11px] font-semibold text-[var(--lp-accent)]">Most popular</span>
                )}
              </div>
              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="text-4xl font-semibold tracking-[-0.03em] text-[var(--lp-ink)]">
                  {annual ? p.price.annual : p.price.monthly}
                </span>
                {p.name !== 'Free' && (
                  <span className="text-[13px] text-[var(--lp-low)]">/month{p.unit ? ` ${p.unit}` : ''}</span>
                )}
              </div>
              <p className="mt-1.5 text-[13px] text-[var(--lp-low)]">{p.note}</p>
              <button onClick={onGoToSignUp} className={`mt-6 w-full justify-center ${p.highlight ? 'btn-primary' : 'btn-outline'}`}>
                {p.cta}
              </button>
              <ul className="mt-7 flex flex-col gap-3 border-t border-[var(--lp-grout)] pt-6">
                {p.features.map(f => (
                  <li key={f.t} className={`flex gap-2.5 text-[13px] ${f.ok ? 'text-[var(--lp-mid)]' : 'text-[var(--lp-low)]'}`}>
                    {f.ok
                      ? <Check className="mt-[3px] h-3.5 w-3.5 flex-none text-[var(--lp-accent)]" />
                      : <Minus className="mt-[3px] h-3.5 w-3.5 flex-none text-[var(--lp-low)]" />}
                    {f.t}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-[13px] text-[var(--lp-low)]">
          Cancel any time and keep your data. Prices in euros, tax included where it applies.
        </p>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <p className="eyebrow" style={{ color: 'var(--t-gold)' }}>FAQ</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
            Questions worth asking first
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[var(--lp-mid)]">
            Anything else, email{' '}
            <a href="mailto:hello@mozayc.io" className="text-[var(--lp-accent)] hover:underline">hello@mozayc.io</a>.
          </p>
        </div>
        <div className="divide-y divide-[var(--lp-grout)] border-y border-[var(--lp-grout)]">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q}>
                <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left">
                  <span className="text-[14.5px] font-medium text-[var(--lp-ink)]">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 flex-none text-[var(--lp-low)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <p className="-mt-1 pb-5 pr-8 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onGoToSignUp }: Pick<LandingPageProps, 'onGoToSignUp'>) {
  return (
    <section className="border-b border-[var(--lp-grout)] py-20 sm:py-28">
      <div className="mx-auto max-w-2xl px-5 text-center">
        <h2 className="text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
          Put next year on one screen
        </h2>
        <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-[var(--lp-mid)]">
          Bring your existing calendars with you. It takes about two minutes.
        </p>
        <button onClick={onGoToSignUp} className="btn-primary btn-lg group mt-8">
          Start free
          <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
        <p className="mt-3.5 text-[13px] text-[var(--lp-low)]">
          Free forever for one person. No credit card. Cancel any time.
        </p>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="py-12">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-5 sm:flex-row sm:items-center sm:justify-between">
        <Logo size="sm" />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[var(--lp-low)]">
          <a href="#features" className="hover:text-[var(--lp-mid)]">Features</a>
          <a href="/pricing" className="hover:text-[var(--lp-mid)]">Pricing</a>
          <a href="/privacy" className="hover:text-[var(--lp-mid)]">Privacy</a>
          <a href="/terms" className="hover:text-[var(--lp-mid)]">Terms</a>
          <a href="mailto:hello@mozayc.io" className="hover:text-[var(--lp-mid)]">Contact</a>
        </div>
        <p className="text-[12.5px] text-[var(--lp-low)]">© {new Date().getFullYear()} Mozayc</p>
      </div>
    </footer>
  );
}

/* FAQPage structured data. Note: injected client-side, so only crawlers that
   execute JS will see it. Prerendering the marketing pages (plan, Phase 6)
   makes it reliable. */
function FaqSchema() {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

function LandingPage({ onGoToLogin, onGoToSignUp, isLoggedIn }: LandingPageProps) {
  return (
    <LandingChrome>

      <FaqSchema />
      <Nav onGoToLogin={onGoToLogin} onGoToSignUp={onGoToSignUp} isLoggedIn={isLoggedIn} />
      <main>
        <Hero onGoToSignUp={onGoToSignUp} />
        <Problem />
        <Solution />
        <HowItWorks onGoToSignUp={onGoToSignUp} />
        <Features />
        <Audiences />
        <Pricing onGoToSignUp={onGoToSignUp} />
        <Faq />
        <FinalCta onGoToSignUp={onGoToSignUp} />
      </main>
      <Footer />
    </LandingChrome>
  );
}

export { LandingPage };
export default LandingPage;
